import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthFetch, QuotaExceededError } from '../hooks/useAuthFetch';
import { SuggestionCarousel } from '../components/agent/SuggestionCarousel';
import { MCP_SAFE_AGENT_PROMPTS } from '../components/agent/mcpPromptSuggestions';
import './Pages.css';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  toolCalls?: string[];
  tokensUsed?: number;
}

interface Session {
  sessionId: string;
  preview: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

interface TokenBudget {
  allowed: boolean;
  tokensUsedToday: number;
  dailyLimit: number;
  remaining: number;
}

export const AgentPage: React.FC = () => {
  const authFetch = useAuthFetch();
  const location = useLocation();
  const navigate = useNavigate();

  // Chat state
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isQuotaError, setIsQuotaError] = useState(false);

  // Sidebar state
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Token budget
  const [budget, setBudget] = useState<TokenBudget | null>(null);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Load sessions and budget on mount
  useEffect(() => {
    fetchSessions();
    fetchBudget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed conversation from top-bar prompt if navigated with state
  useEffect(() => {
    const state = location.state as { query?: string; response?: string; sessionId?: string } | null;
    if (state?.query && state?.response) {
      setMessages([
        { role: 'user', content: state.query },
        { role: 'assistant', content: state.response },
      ]);
      if (state.sessionId) setSessionId(state.sessionId);
      // Clear location state so refresh doesn't re-seed
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await authFetch('/api/agent/sessions');
      if (res.ok) {
        const data = await res.json();
        setSessions(data.sessions || []);
      }
    } catch {
      // silent
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchBudget = async () => {
    try {
      const res = await authFetch('/api/agent/token-budget');
      if (res.ok) {
        setBudget(await res.json());
      }
    } catch {
      // silent
    }
  };

  const loadSession = async (sid: string) => {
    try {
      const res = await authFetch(`/api/agent/sessions/${sid}`);
      if (res.ok) {
        const data = await res.json();
        setSessionId(sid);
        setMessages(data.messages || []);
        setSidebarOpen(false);
      }
    } catch {
      setError('Failed to load conversation');
    }
  };

  const deleteSession = async (sid: string) => {
    try {
      await authFetch(`/api/agent/sessions/${sid}`, { method: 'DELETE' });
      setSessions(prev => prev.filter(s => s.sessionId !== sid));
      if (sessionId === sid) {
        startNewChat();
      }
    } catch {
      // silent
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setError(null);
    inputRef.current?.focus();
  };

  const sendMessage = async (presetMessage?: string) => {
    const msg = (presetMessage ?? input).trim();
    if (!msg || sending) return;

    setInput('');
    setError(null);
    setSending(true);

    // Add user message immediately
    const userMsg: Message = { role: 'user', content: msg };
    setMessages(prev => [...prev, userMsg]);

    try {
      const res = await authFetch('/api/agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          session_id: sessionId,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({ detail: 'Agent request failed' }));
        throw new Error(typeof errData.detail === 'string' ? errData.detail : 'Agent request failed');
      }

      const data = await res.json();

      // Update session ID
      if (data.sessionId) {
        setSessionId(data.sessionId);
      }

      // Add assistant response
      const assistantMsg: Message = {
        role: 'assistant',
        content: data.response,
        toolCalls: data.toolCalls,
        tokensUsed: data.tokensUsed,
      };
      setMessages(prev => [...prev, assistantMsg]);

      // Refresh budget
      fetchBudget();
      // Refresh sessions list
      fetchSessions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
      if (err instanceof QuotaExceededError) setIsQuotaError(true);
      // Remove the optimistic user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
      // Use setTimeout to ensure focus happens after React re-render
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Simple markdown renderer for agent responses
  const renderMarkdown = (text: string) => {
    // Process code blocks
    let html = text
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="agent-code-block"><code>$2</code></pre>')
      .replace(/`([^`]+)`/g, '<code class="agent-inline-code">$1</code>');

    // Process tables
    const lines = html.split('\n');
    let inTable = false;
    const processed: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.startsWith('|') && line.endsWith('|')) {
        if (!inTable) {
          processed.push('<div class="agent-table-wrap"><table class="agent-table">');
          inTable = true;
        }
        // Skip separator rows
        if (/^\|[\s\-:|]+\|$/.test(line)) continue;

        const cells = line.split('|').filter(c => c.trim() !== '');
        const tag = !inTable || processed[processed.length - 1].includes('<table') ? 'th' : 'td';
        processed.push('<tr>' + cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>');
      } else {
        if (inTable) {
          processed.push('</table></div>');
          inTable = false;
        }
        processed.push(line);
      }
    }
    if (inTable) processed.push('</table></div>');

    html = processed.join('\n');

    // Process bold, italic, headers, lists
    html = html
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/^### (.+)$/gm, '<h4 class="agent-h4">$1</h4>')
      .replace(/^## (.+)$/gm, '<h3 class="agent-h3">$1</h3>')
      .replace(/^# (.+)$/gm, '<h2 class="agent-h2">$1</h2>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/^\d+\. (.+)$/gm, '<li>$1</li>');

    // Wrap consecutive <li> in <ul>
    html = html.replace(/(<li>[\s\S]*?<\/li>\n?)+/g, (match) => `<ul class="agent-list">${match}</ul>`);

    // Paragraphs for remaining text
    html = html.replace(/\n\n/g, '</p><p>');
    html = `<p>${html}</p>`;
    html = html.replace(/<p>\s*<\/p>/g, '');

    return html;
  };

  return (
    <div className="upload-page">

      <main className="agent-page-main">
        {/* Sidebar toggle for mobile */}
        <button
          className="agent-sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          title="Conversation history"
        >
          {sidebarOpen ? '✕' : '☰'}
        </button>

        {/* Sidebar - Conversation History */}
        <aside className={`agent-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="agent-sidebar-header">
            <h3>Conversations</h3>
            <button className="agent-new-chat-btn" onClick={startNewChat} title="New conversation">
              + New
            </button>
          </div>

          {loadingSessions ? (
            <div className="agent-sidebar-loading">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="agent-sidebar-empty">No conversations yet</div>
          ) : (
            <div className="agent-session-list">
              {sessions.map(s => (
                <div
                  key={s.sessionId}
                  className={`agent-session-item ${sessionId === s.sessionId ? 'active' : ''}`}
                  onClick={() => loadSession(s.sessionId)}
                >
                  <div className="agent-session-preview">{s.preview}</div>
                  <div className="agent-session-meta">
                    {s.messageCount} messages
                    <button
                      className="agent-session-delete"
                      onClick={(e) => { e.stopPropagation(); deleteSession(s.sessionId); }}
                      title="Delete"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Token Budget */}
          {budget && (
            <div className="agent-budget">
              <div className="agent-budget-label">Daily Token Budget</div>
              <div className="agent-budget-bar">
                <div
                  className="agent-budget-fill"
                  style={{ width: `${Math.min(100, (budget.tokensUsedToday / budget.dailyLimit) * 100)}%` }}
                />
              </div>
              <div className="agent-budget-text">
                {((budget.remaining / 1000) | 0)}K remaining
              </div>
            </div>
          )}
        </aside>

        {/* Main Chat Area */}
        <div className="agent-chat-area">
          {/* Messages */}
          <div className="agent-messages">
            {messages.length === 0 ? (
              <div className="agent-welcome">
                <div className="agent-welcome-icon">🤖</div>
                <h2>xRank HR Assistant</h2>
                <p>I can help you manage resumes, job descriptions, and find the best candidates. Try asking:</p>
                <SuggestionCarousel
                  prompts={MCP_SAFE_AGENT_PROMPTS}
                  onSelect={(suggestion) => {
                    void sendMessage(suggestion);
                  }}
                />
              </div>
            ) : (
              messages.map((msg, i) => (
                <div key={i} className={`agent-message agent-message-${msg.role}`}>
                  <div className="agent-message-avatar">
                    {msg.role === 'user' ? '👤' : '🤖'}
                  </div>
                  <div className="agent-message-content">
                    {msg.role === 'assistant' ? (
                      <div
                        className="agent-markdown"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                      />
                    ) : (
                      <div className="agent-user-text">{msg.content}</div>
                    )}
                    {msg.toolCalls && msg.toolCalls.length > 0 && (
                      <div className="agent-tool-badges">
                        {msg.toolCalls.map((tool, j) => (
                          <span key={j} className="agent-tool-badge">
                            🔧 {tool.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {/* Typing indicator */}
            {sending && (
              <div className="agent-message agent-message-assistant">
                <div className="agent-message-avatar">🤖</div>
                <div className="agent-message-content">
                  <div className="agent-typing">
                    <span className="agent-typing-dot" />
                    <span className="agent-typing-dot" />
                    <span className="agent-typing-dot" />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error */}
          {error && (
            <div className="agent-error">
              ⚠️ {error}
              {isQuotaError && (
                <a href="#/billing" style={{ marginLeft: 8, color: 'var(--accent)', fontWeight: 600 }}>Upgrade to Pro →</a>
              )}
              <button onClick={() => { setError(null); setIsQuotaError(false); }}>✕</button>
            </div>
          )}

          {/* Input Area */}
          <div className="agent-input-area">
            <div className="agent-input-container">
              <textarea
                ref={inputRef}
                className="agent-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about your resumes, job descriptions, or matching..."
                rows={1}
                disabled={sending}
              />
              <button
                className="agent-send-btn"
                onClick={() => { void sendMessage(); }}
                disabled={!input.trim() || sending}
                title="Send message (Enter)"
              >
                {sending ? (
                  <span className="agent-send-spinner" />
                ) : (
                  '➤'
                )}
              </button>
            </div>
            <div className="agent-input-hint">
              Press Enter to send · Shift+Enter for new line
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
